"use client";

import React, { useEffect, useMemo } from "react";
import { UnifiedAppProvider, StubProviders } from "@team-shiphouse/shiphouse-sdk/client";
import { getStoreId, isDevelopment } from "@/config/env";

type PreviewRouteContext = {
  site: string;
  page: string;
  additionalPath: string[];
  fullPath: string[];
  fullRoute: string;
  searchParams?: Record<string, string>;
};

export default function WebBuilderProvider({
  children,
  sdkConfig,
  routeContext,
  previewMode = true,
}: {
  children: React.ReactNode;
  sdkConfig?: { storeId?: string; testMode?: boolean };
  routeContext?: PreviewRouteContext;
  previewMode?: boolean;
}) {
  const unifiedConfig = {
    storeId: sdkConfig?.storeId ?? getStoreId(),
    testMode: sdkConfig?.testMode ?? isDevelopment(),
  };

  // Mock route context for preview mode
  const resolvedRouteContext = useMemo<PreviewRouteContext>(() => routeContext || ({
    site: "preview",
    page: "canvas",
    additionalPath: [],
    fullPath: ["preview", "canvas"],
    fullRoute: "/preview/canvas",
  }), [routeContext]);

  if (typeof globalThis !== "undefined") {
    (globalThis as any).__SHIPHOUSE_WEBBUILDER_PREVIEW__ = previewMode;
  }

  useEffect(() => {
    (globalThis as any).__SHIPHOUSE_WEBBUILDER_PREVIEW__ = previewMode;
    return () => {
      if ((globalThis as any).__SHIPHOUSE_WEBBUILDER_PREVIEW__ === previewMode) {
        delete (globalThis as any).__SHIPHOUSE_WEBBUILDER_PREVIEW__;
      }
    };
  }, [previewMode]);

  return (
    <UnifiedAppProvider
      config={unifiedConfig}
      RouteProvider={StubProviders}
      routeContext={resolvedRouteContext}
    >
      {children}
    </UnifiedAppProvider>
  );
}
